import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Wifi, Navigation, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const recordTypes = ["A", "AAAA", "CNAME", "MX", "NS", "TXT", "SOA", "PTR"];

export default function NetworkTools() {
  const navigate = useNavigate();
  
  // DNS Lookup
  const [domain, setDomain] = useState("");
  const [recordType, setRecordType] = useState("A");
  const [dnsResult, setDnsResult] = useState(null);
  
  // Ping
  const [pingHost, setPingHost] = useState("");
  const [pingCount, setPingCount] = useState("4");
  const [pingResult, setPingResult] = useState(null);
  const [pingLoading, setPingLoading] = useState(false);
  
  // Traceroute
  const [traceHost, setTraceHost] = useState("");
  const [traceMaxHops, setTraceMaxHops] = useState("30");
  const [traceResult, setTraceResult] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);

  const handleDNSLookup = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/network/dns-lookup`, {
        domain,
        record_type: recordType
      });
      setDnsResult(response.data);
      toast.success(`Found ${response.data.count} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "DNS lookup failed");
    }
  };

  const handlePing = async () => {
    if (!pingHost.trim()) {
      toast.error("Please enter a host");
      return;
    }
    
    setPingLoading(true);
    try {
      const response = await axios.post(`${API}/network/ping`, {
        host: pingHost,
        count: parseInt(pingCount)
      });
      setPingResult(response.data);
      toast.success("Ping completed!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ping failed");
    }
    setPingLoading(false);
  };

  const handleTraceroute = async () => {
    if (!traceHost.trim()) {
      toast.error("Please enter a host");
      return;
    }
    
    setTraceLoading(true);
    try {
      const response = await axios.post(`${API}/network/traceroute`, {
        host: traceHost,
        max_hops: parseInt(traceMaxHops)
      });
      setTraceResult(response.data);
      toast.success(`Completed with ${response.data.hop_count} hops`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Traceroute failed");
    }
    setTraceLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Network Tools</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <Tabs defaultValue="dns" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 bg-zinc-800 border-zinc-700 mb-6">
              <TabsTrigger value="dns" data-testid="dns-tab">
                <Search className="w-4 h-4 mr-2" />
                DNS Lookup
              </TabsTrigger>
              <TabsTrigger value="ping" data-testid="ping-tab">
                <Wifi className="w-4 h-4 mr-2" />
                Ping
              </TabsTrigger>
              <TabsTrigger value="traceroute" data-testid="traceroute-tab">
                <Navigation className="w-4 h-4 mr-2" />
                Traceroute
              </TabsTrigger>
            </TabsList>

            {/* DNS Lookup Tab */}
            <TabsContent value="dns" className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="domain" className="text-zinc-400 mb-2 block">Domain</Label>
                  <Input
                    data-testid="domain-input"
                    id="domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="record-type" className="text-zinc-400 mb-2 block">Record Type</Label>
                  <select
                    data-testid="record-type-select"
                    id="record-type"
                    value={recordType}
                    onChange={(e) => setRecordType(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                  >
                    {recordTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Button
                data-testid="dns-lookup-btn"
                onClick={handleDNSLookup}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                Lookup DNS Records
              </Button>

              {dnsResult && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>
                    {recordType} Records for {dnsResult.domain}
                  </h3>
                  {dnsResult.records.length > 0 ? (
                    <div className="space-y-2">
                      {dnsResult.records.map((record, index) => (
                        <div key={index} className="bg-zinc-900/50 p-3 rounded border border-zinc-700">
                          <code className="text-white font-mono text-sm">{record}</code>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">
                      No records found
                    </div>
                  )}
                </Card>
              )}
            </TabsContent>

            {/* Ping Tab */}
            <TabsContent value="ping" className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="ping-host" className="text-zinc-400 mb-2 block">Host</Label>
                  <Input
                    data-testid="ping-host-input"
                    id="ping-host"
                    value={pingHost}
                    onChange={(e) => setPingHost(e.target.value)}
                    placeholder="example.com or 8.8.8.8"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="ping-count" className="text-zinc-400 mb-2 block">Count</Label>
                  <Input
                    data-testid="ping-count-input"
                    id="ping-count"
                    type="number"
                    value={pingCount}
                    onChange={(e) => setPingCount(e.target.value)}
                    placeholder="4"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="ping-btn"
                onClick={handlePing}
                disabled={pingLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                {pingLoading ? "Pinging..." : "Ping Host"}
              </Button>

              {pingResult && (
                <div className="space-y-4">
                  {pingResult.statistics && Object.keys(pingResult.statistics).length > 0 && (
                    <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                      <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Statistics</h3>
                      <div className="grid md:grid-cols-4 gap-4">
                        {pingResult.statistics.transmitted && (
                          <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                            <div className="text-sm text-zinc-400">Transmitted</div>
                            <div className="text-2xl font-bold text-white">{pingResult.statistics.transmitted}</div>
                          </div>
                        )}
                        {pingResult.statistics.received && (
                          <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                            <div className="text-sm text-zinc-400">Received</div>
                            <div className="text-2xl font-bold text-emerald-400">{pingResult.statistics.received}</div>
                          </div>
                        )}
                        {pingResult.statistics.avg && (
                          <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                            <div className="text-sm text-zinc-400">Avg RTT</div>
                            <div className="text-2xl font-bold text-cyan-400">{pingResult.statistics.avg} ms</div>
                          </div>
                        )}
                        {pingResult.statistics.loss && (
                          <div className="bg-zinc-900/50 p-4 rounded border border-zinc-700">
                            <div className="text-sm text-zinc-400">Packet Loss</div>
                            <div className="text-2xl font-bold text-white">{pingResult.statistics.loss}</div>
                          </div>
                        )}
                      </div>
                    </Card>
                  )}

                  <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                    <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>Output</h3>
                    <pre className="bg-zinc-900 p-4 rounded border border-zinc-700 overflow-x-auto text-sm text-zinc-300 max-h-96">
                      {pingResult.output}
                    </pre>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Traceroute Tab */}
            <TabsContent value="traceroute" className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="trace-host" className="text-zinc-400 mb-2 block">Host</Label>
                  <Input
                    data-testid="trace-host-input"
                    id="trace-host"
                    value={traceHost}
                    onChange={(e) => setTraceHost(e.target.value)}
                    placeholder="example.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="max-hops" className="text-zinc-400 mb-2 block">Max Hops</Label>
                  <Input
                    data-testid="max-hops-input"
                    id="max-hops"
                    type="number"
                    value={traceMaxHops}
                    onChange={(e) => setTraceMaxHops(e.target.value)}
                    placeholder="30"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <Button
                data-testid="traceroute-btn"
                onClick={handleTraceroute}
                disabled={traceLoading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                {traceLoading ? "Tracing route..." : "Traceroute"}
              </Button>

              {traceResult && (
                <Card className="bg-zinc-800/50 border-zinc-700 p-6">
                  <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk'}}>
                    Route to {traceResult.host} ({traceResult.hop_count} hops)
                  </h3>
                  <pre className="bg-zinc-900 p-4 rounded border border-zinc-700 overflow-x-auto text-sm text-zinc-300 max-h-96">
                    {traceResult.output}
                  </pre>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}